import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * @param {object} param
 * @param {Terminal} param.terminal
 * @returns {{
 * TERMINAL_FORM_SCHEMA: import('@zolteam/zol-react-form/types').BasicSchema,
 * terminalDefaultValues: object,
 * splitTerminalForm: Function,
 * terminalFormDataToPayload: Function
 * }}
 */
export const useTerminals = ({ terminal }) => {
  const { t } = useTranslation();

  const TERMINAL_FORM_SCHEMA = useMemo(() => ({
    $schema: 'https://json-schema.org/draft/2020-12/schema',
    type: 'object',
    title: 'Terminal',
    properties: {
      name: {
        type: 'string',
        description: t('terminalForm.fields.name'),
        x_layout: '1/1/1/10',
        minLength: '1',
      },
      identityCode: {
        type: 'string',
        description: t('terminalForm.fields.identityCode'),
        x_layout: '2/1/2/5',
        minLength: '1',
      },
      address: {
        type: 'string',
        description: t('terminalForm.fields.address'),
        x_layout: '2/6/2/10',
      },
      coordinate: {
        type: 'array',
        description: t('terminalForm.fields.coordinate'),
        x_layout: '3/1/3/10',
        items: {
          type: 'string',
        },
      },
      phone: {
        type: 'string',
        description: t('terminalForm.fields.phone'),
        x_layout: '4/1/4/5',
      },
      openingTime: {
        type: 'string',
        description: t('terminalForm.fields.openingTime'),
        x_layout: '4/6/4/10',
      },
      closingTime: {
        type: 'string',
        description: t('terminalForm.fields.closingTime'),
        x_layout: '5/1/5/5',
      },
      meetingInfo: {
        type: 'string',
        description: t('terminalForm.fields.meetingInfo'),
        x_layout: '5/6/5/10',
      },
    },
    required: ['name', 'code', 'city'],
  }), [t]);

  const terminalDefaultValues = useMemo(() => ({
    name: terminal?.name || '',
    identityCode: terminal?.identityCode || '',
    address: terminal?.address || '',
    coordinate: terminal?.coordinate || [],
    phone: terminal?.phone || '',
    openingTime: terminal?.openingTime || '',
    closingTime: terminal?.closingTime || '',
    meetingInfo: terminal?.meetingInfo || '',
  }), [terminal]);

  /**
   * @param {object} params - The values from the form.
   * @param {Terminal} params.formValues - The values from the form.
   * @returns {Terminal} Payload
   */
  const terminalFormDataToPayload = ({
    formValues,
  }) => ({
    name: formValues.name,
    identityCode: formValues.identityCode,
    address: formValues.address,
    coordinate: formValues.coordinate,
    phone: formValues.phone,
    openingTime: formValues.openingTime,
    closingTime: formValues.closingTime,
    meetingInfo: formValues.meetingInfo,
  });

  /**
   * Split the airport form into different parts
   * @param {import('@zolteam/zol-react-form/types').ConfigObject} formConfig
   * @returns {{ generalPart: object, terminalsPart: object }} The split terminal form
   */
  const splitTerminalForm = (formConfig) => Object.entries(formConfig)
    .reduce((acc, [key, value]) => {
      if (key?.includes('terminals')) {
        return { ...acc, terminalsPart: { ...acc.terminalsPart, [key]: value } };
      }
      return { ...acc, generalPart: { ...acc.generalPart, [key]: value } };
    }, {
      generalPart: {},
      terminalsPart: {},
    });

  return {
    TERMINAL_FORM_SCHEMA,
    terminalDefaultValues,
    splitTerminalForm,
    terminalFormDataToPayload,
  };
};
